import { create } from 'zustand';
import type {
  EvaluationState,
  ReviewData,
  CriterionData,
  CriterionSaveData,
  GradeData,
  RubricCell,
  RubricAnalysisData,
  OverallEvaluationSettings,
  EvaluationComment
} from '../types';
import { apiClient, withErrorHandling, ApiError } from '../api/client';

// 状態管理インターフェース（currentExaminationIdを削除）
interface EvaluationStore extends EvaluationState {
  // UI状態
  isLoading: boolean;
  isCreatingProject: boolean;
  error: string | null;
  lastSaved: Date | null;
  isReadonly: boolean;

  // 自動保存機能
  autoSaveEnabled: boolean;
  autoSaveInterval: number; // ミリ秒
  hasUnsavedChanges: boolean;
  autoSaveTimer: number | null;

  // 評価タイトル管理を追加
  evaluationTitle: string;
  setEvaluationTitle: (title: string) => void;

  // 現在の評価ID管理を追加
  currentExaminationId: string | null;
  setCurrentExaminationId: (id: string | null) => void;

  // 新しい評価プロジェクト作成
  createNewEvaluation: () => void;

  // 特定の評価プロジェクト読み込み
  loadEvaluationById: (id: string) => Promise<boolean>;

  // Actions - 基本操作
  setCurrentStep: (step: number) => void;

  // Actions - 評価対象管理
  addProduct: (product: ReviewData) => void;
  updateProduct: (index: number, product: Partial<ReviewData>) => void;
  removeProduct: (index: number) => void;
  setProducts: (products: ReviewData[]) => void;
  clearProducts: () => void;

  // Actions - 観点管理
  setCriteria: (criteria: CriterionData[]) => void;
  updateCriterion: (index: number, criterion: CriterionData) => void;
  removeCriterion: (index: number) => void;
  clearCriteria: () => void;

  // Actions - 評価位置管理
  setEvaluationPosition: (productIndex: number, criterionIndex: number, x: number, y: number) => void;
  removeEvaluationPosition: (productIndex: number, criterionIndex: number) => void;
  getEvaluationPosition: (productIndex: number, criterionIndex: number) => { x: number; y: number } | null;
  clearEvaluationPositions: () => void;

  // Actions - 等級管理
  setGrades: (criterionIndex: number, grades: GradeData[]) => void;
  getGrades: (criterionIndex: number) => GradeData[];
  clearGrades: () => void;

  // Actions - 評価コメント管理
  setEvaluationComment: (productIndex: number, criterionIndex: number, comment: string, details?: string) => void;
  getEvaluationComment: (productIndex: number, criterionIndex: number) => EvaluationComment | null;
  removeEvaluationComment: (productIndex: number, criterionIndex: number) => void;
  clearEvaluationComments: () => void;

  // Actions - ルーブリック管理
  setRubricData: (rubricData: RubricCell[][]) => void;
  getRubricData: () => RubricCell[][] | undefined;
  setRubricAnalysis: (criterionIndex: number, analysis: RubricAnalysisData) => void;
  getRubricAnalysis: (criterionIndex: number) => RubricAnalysisData | undefined;
  clearRubricData: () => void;

  // Actions - 総合評価設定管理
  setOverallEvaluationSettings: (settings: OverallEvaluationSettings) => void;
  getOverallEvaluationSettings: () => OverallEvaluationSettings | undefined;
  clearOverallEvaluationSettings: () => void;

  // Actions - API操作（シンプル化）
  extractCriteriaAsync: (descriptions: string[]) => Promise<boolean>;
  saveDataAsync: () => Promise<boolean>;
  loadDataAsync: () => Promise<boolean>;
  deleteDataAsync: () => Promise<boolean>;

  // 新しい保存メソッド
  createNewProjectWithData: () => Promise<boolean>;
  updateExistingProject: () => Promise<boolean>;
  convertCriteriaForSave: (criteria: CriterionData[], products: ReviewData[], evaluationPositions: Map<string, { x: number; y: number }>, grades: Map<string, GradeData[]>) => CriterionSaveData[];
  loadRubricDataSafely: (id: string) => Promise<void>;

  // Actions - リセット
  resetAll: () => void;
  resetCurrentEvaluation: () => void;

  // Actions - プロジェクト管理
  startNewProject: (title?: string) => void;
  saveNewProject: () => Promise<boolean>; // 初回保存用

  // Actions - 自動保存管理
  enableAutoSave: (enabled: boolean) => void;
  setAutoSaveInterval: (interval: number) => void;
  markAsChanged: () => void;
  triggerAutoSave: () => void;
  clearAutoSaveTimer: () => void;
  
  // Actions - 進捗確認
  showProgress: () => void;

  // Actions - 読み取り専用モード
  setReadonly: (readonly: boolean) => void;
}

// ヘルパー関数: 進捗に基づいてステップを自動判定
const detectCurrentStep = (
  products: ReviewData[], 
  criteria: CriterionData[], 
  evaluationPositions: Map<string, { x: number; y: number }>,
  grades: Map<string, GradeData[]>,
  hasRubric: boolean = false
): number => {
  // 評価対象がない場合はステップ1
  if (!products || products.length === 0) return 1;

  // 観点がない場合はステップ2
  if (!criteria || criteria.length === 0) return 2;

  // 評価位置情報があるか確認
  let hasPositions = false;
  let allCriteriaHaveGrades = true;
  let allProductsEvaluated = true;

  criteria.forEach((_, criterionIndex) => {
    // 位置情報の確認
    const hasValidPositions = products.some((_, productIndex) => {
      const position = evaluationPositions.get(`${productIndex}-${criterionIndex}`);
      return position && position.x !== -1;
    });
    
    if (hasValidPositions) {
      hasPositions = true;
    }

    // すべての製品が評価されているかチェック
    const evaluatedCount = products.filter((_, productIndex) => {
      const position = evaluationPositions.get(`${productIndex}-${criterionIndex}`);
      return position && position.x !== -1;
    }).length;
    
    if (evaluatedCount < products.length) {
      allProductsEvaluated = false;
    }

    // 等級情報の確認
    const criterionGrades = grades.get(criterionIndex.toString());
    if (!criterionGrades || criterionGrades.length === 0) {
      allCriteriaHaveGrades = false;
    }
  });

  // 評価位置がない場合はステップ3（いくつか、良い順にならべてみましょう）
  if (!hasPositions) return 3;

  // 等級設定がない場合はステップ4（等級設定）
  if (!allCriteriaHaveGrades) return 4;

  // ルーブリックが生成されている場合
  if (hasRubric) {
    // すべての製品が評価されていればステップ7（成績表出力）
    if (allProductsEvaluated) return 7;
    // 評価されていない製品があればステップ6（クイック評価）
    return 6;
  }

  // 等級設定まで完了していればステップ5（ルーブリック生成）
  return 5;
};

// 詳細な進捗状況を取得して次のアクションを提案
const getDetailedProgress = (
  products: ReviewData[], 
  criteria: CriterionData[], 
  evaluationPositions: Map<string, { x: number; y: number }>,
  grades: Map<string, GradeData[]>,
  currentStep: number,
  hasRubric: boolean = false
): void => {
  console.group('🎯 評価進捗状況と次のアクション');
  
  // ステップ1: 製品登録
  if (currentStep === 1) {
    console.log('📝 現在のステップ: 製品登録');
    console.log(`✅ 登録済み製品数: ${products.length}`);
    if (products.length === 0) {
      console.log('💡 次のアクション: 評価対象となる製品を登録してください');
    } else {
      console.log('💡 次のアクション: 製品登録を完了して「次へ」をクリック');
    }
  }
  
  // ステップ2: 観点抽出
  else if (currentStep === 2) {
    console.log('🔍 現在のステップ: 観点抽出');
    console.log(`✅ 登録済み観点数: ${criteria.length}`);
    if (criteria.length === 0) {
      console.log('💡 次のアクション: 評価観点を抽出または手動で追加してください');
    } else {
      console.log('💡 次のアクション: 観点の確認・編集を行い「次へ」をクリック');
    }
  }
  
  // ステップ3: 比較評価
  else if (currentStep === 3) {
    console.log('⚖️ 現在のステップ: 比較評価');
    console.log(`製品数: ${products.length}, 観点数: ${criteria.length}`);
    console.log('\n📊 観点ごとの配置状況:');
    
    criteria.forEach((criterion, criterionIndex) => {
      const placedProducts = products.filter((_, productIndex) => {
        const position = evaluationPositions.get(`${productIndex}-${criterionIndex}`);
        return position && position.x !== -1;
      });
      
      const placedCount = placedProducts.length;
      const totalCount = products.length;
      const percentage = totalCount > 0 ? Math.round((placedCount / totalCount) * 100) : 0;
      
      console.log(`  ${criterionIndex + 1}. ${criterion.title}:`);
      console.log(`     配置済み: ${placedCount}/${totalCount} (${percentage}%)`);
      
      if (placedCount < totalCount && placedCount <= 5) {
        const unplacedProducts = products.filter((_, productIndex) => {
          const position = evaluationPositions.get(`${productIndex}-${criterionIndex}`);
          return !position || position.x === -1;
        });
        
        const unplacedNames = unplacedProducts.slice(0, 5).map(p => p.reviewTitle).join(', ');
        console.log(`     未配置: ${unplacedNames}${unplacedProducts.length > 5 ? '...' : ''}`);
      }
    });
    
    // 必要な比較数（全体の10%または5個の多い方）
    const requiredComparisons = Math.max(5, Math.ceil(products.length * 0.1));
    console.log(`\n📌 必要な比較数: 各観点${requiredComparisons}個以上`);
    
    // 十分な配置がない観点を探す
    const insufficientCriteria = criteria.filter((criterion, criterionIndex) => {
      const placedCount = products.filter((_, productIndex) => {
        const position = evaluationPositions.get(`${productIndex}-${criterionIndex}`);
        return position && position.x !== -1;
      }).length;
      return placedCount < requiredComparisons;
    });
    
    if (insufficientCriteria.length === criteria.length) {
      console.log(`\n💡 次のアクション: 各観点で少なくとも${requiredComparisons}個の製品を配置してください`);
    } else if (insufficientCriteria.length > 0) {
      const firstInsufficient = insufficientCriteria[0];
      const placedCount = products.filter((_, productIndex) => {
        const position = evaluationPositions.get(`${productIndex}-${criteria.indexOf(firstInsufficient)}`);
        return position && position.x !== -1;
      }).length;
      console.log(`\n💡 次のアクション: 「${firstInsufficient.title}」の観点であと${requiredComparisons - placedCount}個の製品を配置してください`);
    } else {
      console.log('\n💡 次のアクション: 必要な比較が完了しました。「次へ」をクリックして等級設定へ');
    }
  }
  
  // ステップ4: 等級設定
  else if (currentStep === 4) {
    console.log('📊 現在のステップ: 等級設定');
    const criteriaWithGrades = criteria.filter((_, index) => {
      const criterionGrades = grades.get(index.toString());
      return criterionGrades && criterionGrades.length > 0;
    });
    
    console.log(`✅ 等級設定済み: ${criteriaWithGrades.length}/${criteria.length} 観点`);
    
    const incompleteCriteria = criteria.filter((_, index) => {
      const criterionGrades = grades.get(index.toString());
      return !criterionGrades || criterionGrades.length === 0;
    });
    
    if (incompleteCriteria.length > 0) {
      console.log('\n📋 等級未設定の観点:');
      incompleteCriteria.slice(0, 5).forEach((criterion, i) => {
        console.log(`  - ${criterion.title}`);
      });
      if (incompleteCriteria.length > 5) {
        console.log(`  ... 他${incompleteCriteria.length - 5}件`);
      }
      console.log('\n💡 次のアクション: 各観点の等級（A,B,C等）を設定してください');
    } else {
      console.log('\n💡 次のアクション: 等級設定が完了しました。「次へ」をクリックしてルーブリック生成へ');
    }
  }
  
  // ステップ5: ルーブリック生成
  else if (currentStep === 5) {
    console.log('📝 現在のステップ: ルーブリック生成');
    console.log('💡 次のアクション: ルーブリックを生成してください');
  }
  
  // ステップ6: クイック評価
  else if (currentStep === 6) {
    console.log('⚡ 現在のステップ: クイック評価（未評価製品の処理）');
    console.log('\n📊 観点ごとの未評価状況:');
    
    let totalUnplaced = 0;
    criteria.forEach((criterion, criterionIndex) => {
      const unplacedProducts = products.filter((_, productIndex) => {
        const position = evaluationPositions.get(`${productIndex}-${criterionIndex}`);
        return !position || position.x === -1;
      });
      
      if (unplacedProducts.length > 0) {
        totalUnplaced += unplacedProducts.length;
        console.log(`  ${criterion.title}:`);
        console.log(`    未評価: ${unplacedProducts.length}件`);
        const productNames = unplacedProducts.slice(0, 3).map(p => p.reviewTitle).join(', ');
        console.log(`    製品: ${productNames}${unplacedProducts.length > 3 ? '...' : ''}`);
      }
    });
    
    if (totalUnplaced > 0) {
      console.log(`\n💡 次のアクション: 残り${totalUnplaced}件の評価を完了させてください`);
    } else {
      console.log('\n💡 次のアクション: すべての評価が完了しました。「次へ」をクリックして成績表出力へ');
    }
  }
  
  // ステップ7: 成績表出力
  else if (currentStep === 7) {
    console.log('📄 現在のステップ: 成績表出力');
    console.log('✅ すべての評価が完了しています');
    console.log('💡 次のアクション: 成績表をダウンロードまたは共有してください');
  }
  
  console.groupEnd();
};

// 初期状態
const initialState: EvaluationState = {
  currentStep: 1,
  products: [],
  criteria: [],
  evaluationPositions: new Map(),
  grades: new Map(),
  evaluationComments: new Map(),
  rubricData: undefined,
  rubricAnalysis: new Map(),
  overallEvaluationSettings: undefined,
};

// Zustandストア作成（currentExaminationId管理を削除）
export const useEvaluationStore = create<EvaluationStore>()(
  (set, get) => ({
      // 初期状態
      ...initialState,
      isLoading: false,
      isCreatingProject: false,
      error: null,
      lastSaved: null,
      isReadonly: false,
      evaluationTitle: '',
      currentExaminationId: null,

      // 自動保存機能の初期状態
      autoSaveEnabled: true,
      autoSaveInterval: 60000, // 60秒（1分）に延長
      hasUnsavedChanges: false,
      autoSaveTimer: null,

      // 基本操作
      setCurrentStep: (step) => {
        set({ currentStep: step, error: null });
        // 読み込み中は進捗状況を出力しない
        if (!get().isLoading) {
          getDetailedProgress(
            get().products,
            get().criteria,
            get().evaluationPositions,
            get().grades,
            step,
            get().rubricData !== undefined
          );
        }
      },

      // 評価タイトル管理
      setEvaluationTitle: (title) => {
        set({ evaluationTitle: title, error: null });
        // タイトル変更はすぐには保存しない
        // get().markAsChanged();
      },

      // 現在の評価ID管理
      setCurrentExaminationId: (id) => {
        set({ currentExaminationId: id, error: null });
      },

      // 評価対象管理
      addProduct: (product) => {
        set((state) => ({
          products: [product, ...state.products],
          error: null
        }));
        get().markAsChanged();
      },

      updateProduct: (index, product) => {
        set((state) => {
          const newProducts = [...state.products];
          newProducts[index] = { ...newProducts[index], ...product };
          return { products: newProducts, error: null };
        });
        get().markAsChanged();
      },

      removeProduct: (index) => {
        set((state) => {
          const newProducts = state.products.filter((_, i) => i !== index);
          const newPositions = new Map(state.evaluationPositions);

          // 削除された評価対象の位置情報も削除
          for (const [key] of newPositions) {
            if (key.startsWith(`${index}-`)) {
              newPositions.delete(key);
            }
          }

          // インデックスを詰める
          const updatedPositions = new Map();
          for (const [key, value] of newPositions) {
            const [productIndexStr, criterionIndexStr] = key.split('-');
            const productIndex = parseInt(productIndexStr);
            const criterionIndex = parseInt(criterionIndexStr);

            if (productIndex > index) {
              updatedPositions.set(`${productIndex - 1}-${criterionIndex}`, value);
            } else if (productIndex < index) {
              updatedPositions.set(key, value);
            }
          }

          return {
            products: newProducts,
            evaluationPositions: updatedPositions,
            error: null
          };
        });
        get().markAsChanged();
      },

      setProducts: (products) => {
        set({ products, error: null });
        get().markAsChanged();
      },

      clearProducts: () => {
        set({ products: [], evaluationPositions: new Map(), error: null });
        get().markAsChanged();
      },

      // 観点管理
      setCriteria: (criteria) => {
        set({ criteria, error: null });
        get().markAsChanged();
      },

      updateCriterion: (index, criterion) => {
        set((state) => {
          const newCriteria = [...state.criteria];
          newCriteria[index] = criterion;
          return { criteria: newCriteria, error: null };
        });
        get().markAsChanged();
      },

      removeCriterion: (index) => {
        set((state) => {
          const newCriteria = state.criteria.filter((_, i) => i !== index);
          const newPositions = new Map(state.evaluationPositions);
          const newGrades = new Map(state.grades);

          // 削除された観点の位置情報と等級を削除
          for (const [key] of newPositions) {
            if (key.endsWith(`-${index}`)) {
              newPositions.delete(key);
            }
          }
          newGrades.delete(index.toString());

          // インデックスを詰める
          const updatedPositions = new Map();
          for (const [key, value] of newPositions) {
            const [productIndexStr, criterionIndexStr] = key.split('-');
            const productIndex = parseInt(productIndexStr);
            const criterionIndex = parseInt(criterionIndexStr);

            if (criterionIndex > index) {
              updatedPositions.set(`${productIndex}-${criterionIndex - 1}`, value);
            } else if (criterionIndex < index) {
              updatedPositions.set(key, value);
            }
          }

          const updatedGrades = new Map();
          for (const [key, value] of newGrades) {
            const criterionIndex = parseInt(key);
            if (criterionIndex > index) {
              updatedGrades.set((criterionIndex - 1).toString(), value);
            } else if (criterionIndex < index) {
              updatedGrades.set(key, value);
            }
          }

          return {
            criteria: newCriteria,
            evaluationPositions: updatedPositions,
            grades: updatedGrades,
            error: null
          };
        });
        get().markAsChanged();
      },

      clearCriteria: () => {
        set({ criteria: [], evaluationPositions: new Map(), grades: new Map(), error: null });
        get().markAsChanged();
      },

      // 評価位置管理
      setEvaluationPosition: (productIndex, criterionIndex, x, y) => {
        set((state) => {
          const newPositions = new Map(state.evaluationPositions);
          newPositions.set(`${productIndex}-${criterionIndex}`, { x, y });
          return { evaluationPositions: newPositions, error: null };
        });
        get().markAsChanged();
      },

      removeEvaluationPosition: (productIndex, criterionIndex) => {
        set((state) => {
          const newPositions = new Map(state.evaluationPositions);
          newPositions.delete(`${productIndex}-${criterionIndex}`);
          return { evaluationPositions: newPositions, error: null };
        });
        get().markAsChanged();
      },

      getEvaluationPosition: (productIndex, criterionIndex) => {
        const state = get();
        return state.evaluationPositions.get(`${productIndex}-${criterionIndex}`) || null;
      },

      clearEvaluationPositions: () => {
        set({ evaluationPositions: new Map(), error: null });
        get().markAsChanged();
      },

      // 等級管理
      setGrades: (criterionIndex, grades) => {
        set((state) => {
          const newGrades = new Map(state.grades);
          newGrades.set(criterionIndex.toString(), grades);
          return { grades: newGrades, error: null };
        });
        get().markAsChanged();
      },

      getGrades: (criterionIndex) => {
        const state = get();
        return state.grades.get(criterionIndex.toString()) || [];
      },

      clearGrades: () => {
        set({ grades: new Map(), error: null });
        get().markAsChanged();
      },

      // 評価コメント管理
      setEvaluationComment: (productIndex, criterionIndex, comment, details) => {
        set((state) => {
          const newComments = new Map(state.evaluationComments);
          const key = `${productIndex}-${criterionIndex}`;
          newComments.set(key, {
            productIndex,
            criterionIndex,
            comment,
            details
          });
          return { evaluationComments: newComments, error: null };
        });
        get().markAsChanged();
      },

      getEvaluationComment: (productIndex, criterionIndex) => {
        const state = get();
        const key = `${productIndex}-${criterionIndex}`;
        return state.evaluationComments.get(key) || null;
      },

      removeEvaluationComment: (productIndex, criterionIndex) => {
        set((state) => {
          const newComments = new Map(state.evaluationComments);
          const key = `${productIndex}-${criterionIndex}`;
          newComments.delete(key);
          return { evaluationComments: newComments, error: null };
        });
        get().markAsChanged();
      },

      clearEvaluationComments: () => {
        set({ evaluationComments: new Map(), error: null });
        get().markAsChanged();
      },

      // ルーブリック管理
      setRubricData: (rubricData) => {
        set({ rubricData, error: null });
        get().markAsChanged();
      },

      getRubricData: () => {
        const state = get();
        return state.rubricData;
      },

      setRubricAnalysis: (criterionIndex, analysis) => {
        set((state) => {
          const newAnalysis = new Map(state.rubricAnalysis);
          newAnalysis.set(criterionIndex.toString(), analysis);
          return { rubricAnalysis: newAnalysis, error: null };
        });
        get().markAsChanged();
      },

      getRubricAnalysis: (criterionIndex) => {
        const state = get();
        return state.rubricAnalysis?.get(criterionIndex.toString());
      },

      clearRubricData: () => {
        set({ rubricData: undefined, rubricAnalysis: new Map(), error: null });
        get().markAsChanged();
      },

      // 総合評価設定管理
      setOverallEvaluationSettings: (settings) => {
        set({ overallEvaluationSettings: settings, error: null });
        get().markAsChanged();
      },

      getOverallEvaluationSettings: () => {
        const state = get();
        return state.overallEvaluationSettings;
      },

      clearOverallEvaluationSettings: () => {
        set({ overallEvaluationSettings: undefined, error: null });
        get().markAsChanged();
      },

      // API操作（シンプル化：毎回最新データの上書き保存・読み込み）
      extractCriteriaAsync: async (descriptions) => {
        console.log('extractCriteriaAsync called');
        const { products } = get();
        set({ isLoading: true, error: null });
        console.log('Loading state set to true');

        const result = await withErrorHandling(
          () => apiClient.extractCriteria(descriptions, products),
          (error: ApiError) => set({ error: `観点抽出エラー: ${error.message}` })
        );

        console.log('Loading state set to false');
        set({ isLoading: false });

        if (result && result.length > 0) {
          set((state) => ({ criteria: result }));
          return true;
        }

        return false;
      },

      saveDataAsync: async () => {
        const { products, criteria, evaluationPositions, grades, evaluationTitle, currentExaminationId, isLoading, isCreatingProject } = get();
        
        // 既に保存中またはプロジェクト作成中の場合は早期リターン
        if (isLoading || isCreatingProject) {
          console.log('既に保存中またはプロジェクト作成中のため、スキップします');
          return false;
        }

        // 保存するデータがない場合は何もしない
        if (products.length === 0 && criteria.length === 0) {
          console.log('保存するデータがありません');
          return false;
        }
        
        console.log('saveDataAsync開始', {
          currentExaminationId,
          evaluationTitle,
          productsLength: products.length,
          criteriaLength: criteria.length
        });
        
        set({ isLoading: true, error: null });

        try {
          // 新規プロジェクトの場合
          if (!currentExaminationId) {
            return await get().createNewProjectWithData();
          }

          // 既存プロジェクトの更新
          return await get().updateExistingProject();
        } catch (error) {
          console.error('保存エラー:', error);
          set({ 
            isLoading: false,
            error: `保存エラー: ${error instanceof Error ? error.message : '不明なエラー'}`
          });
          return false;
        }
      },

      loadDataAsync: async () => {
        const { currentExaminationId } = get();
        
        if (currentExaminationId) {
          // loadEvaluationByIdを使用して読み込み
          return await get().loadEvaluationById(currentExaminationId);
        } else {
          // レガシー読み込み（統一API）
          const { setProducts, setCriteria, setEvaluationPosition, setGrades, setEvaluationTitle } = get();
          set({ isLoading: true, error: null });
          
          const result = await withErrorHandling(
            () => apiClient.loadData(),
            (error: ApiError) => set({ error: `読み込みエラー: ${error.message}` })
          );

          if (result) {
            // 評価タイトルを復元
            if (result.saveTitle) {
              setEvaluationTitle(result.saveTitle);
            }

            // 統一API形式のデータを復元
            setProducts(result.saveReview || []);
            setCriteria(result.saveCriteria?.map((c: CriterionSaveData) => ({
              title: c.saveCriterion,
              description: c.saveDescription || []
            })) || []);

            // 評価位置を復元
            result.saveCriteria?.forEach((criterion: CriterionSaveData, criterionIndex: number) => {
              criterion.saveXList?.forEach((x, productIndex) => {
                const y = criterion.saveYList?.[productIndex] || 0;
                if (x !== -1) { // -1は未配置を示す
                  setEvaluationPosition(productIndex, criterionIndex, x, y);
                }
              });

              // 等級を復元
              setGrades(criterionIndex, criterion.saveGrade || []);
            });

            set({ isLoading: false });
            return true;
          }

          set({ isLoading: false });
          return false;
        }
      },

      deleteDataAsync: async () => {
        const { resetAll } = get();
        set({ isLoading: true, error: null });

        const result = await withErrorHandling(
          () => apiClient.deleteData(),
          (error: ApiError) => set({ error: `削除エラー: ${error.message}` })
        );

        set({ isLoading: false });

        if (result?.isSuccess) {
          resetAll(); // ローカルデータもクリア
          return true;
        }

        return false;
      },

      // 自動保存機能
      enableAutoSave: (enabled) => {
        const { clearAutoSaveTimer, triggerAutoSave } = get();
        clearAutoSaveTimer();
        set({ autoSaveEnabled: enabled });
        if (enabled) {
          triggerAutoSave();
        }
      },

      setAutoSaveInterval: (interval) => {
        const { autoSaveEnabled, clearAutoSaveTimer, triggerAutoSave } = get();
        set({ autoSaveInterval: interval });
        if (autoSaveEnabled) {
          clearAutoSaveTimer();
          triggerAutoSave();
        }
      },

      markAsChanged: () => {
        const { autoSaveEnabled, hasUnsavedChanges, triggerAutoSave, currentExaminationId, products, criteria } = get();
        
        // 変更フラグを立てる
        if (!hasUnsavedChanges) {
          set({ hasUnsavedChanges: true });
        }
        
        // 自動保存の条件を厳しくする：
        // 1. 自動保存が有効
        // 2. プロジェクトIDが存在する（既存プロジェクトのみ）
        // 3. 保存すべきデータが存在する
        if (autoSaveEnabled && currentExaminationId && (products.length > 0 || criteria.length > 0)) {
          triggerAutoSave();
        }
      },

      triggerAutoSave: () => {
        const { autoSaveInterval, clearAutoSaveTimer, saveDataAsync } = get();
        clearAutoSaveTimer();

        const timer = window.setTimeout(async () => {
          const { hasUnsavedChanges, currentExaminationId, isLoading, isCreatingProject } = get();

          // 自動保存の条件をより厳密にチェック
          if (!hasUnsavedChanges || !currentExaminationId || isLoading || isCreatingProject) {
            return;
          }

          try {
            console.log('自動保存を実行中...');
            const success = await saveDataAsync();
            if (success) {
              console.log('自動保存が完了しました');
            } else {
              console.warn('自動保存が失敗しました（データは保持されます）');
            }
          } catch (error) {
            console.warn('自動保存でエラーが発生しました:', error);
            // エラーが発生してもデータを保持し、手動保存を促す
          }
        }, autoSaveInterval);

        set({ autoSaveTimer: timer });
      },

      clearAutoSaveTimer: () => {
        const { autoSaveTimer } = get();
        if (autoSaveTimer) {
          window.clearTimeout(autoSaveTimer);
          set({ autoSaveTimer: null });
        }
      },
      
      // 進捗確認
      showProgress: () => {
        const state = get();
        getDetailedProgress(
          state.products,
          state.criteria,
          state.evaluationPositions,
          state.grades,
          state.currentStep,
          state.rubricData !== undefined
        );
      },

      // 読み取り専用モード設定
      setReadonly: (readonly: boolean) => {
        set({ isReadonly: readonly });
      },

      // リセット
      resetAll: () => {
        set({
          ...initialState,
          isLoading: false,
          error: null,
          lastSaved: null,
          evaluationTitle: '',
          currentExaminationId: null
        });
      },

      resetCurrentEvaluation: () => {
        set({
          ...initialState,
          error: null,
          evaluationTitle: '',
          currentExaminationId: null
        });
      },

      // 新しい評価プロジェクトを開始
      startNewProject: (title) => {
        set((state) => ({
          ...initialState,
          evaluationTitle: title || '',
          currentExaminationId: null,
          error: null
        }));
      },

      // 新しいプロジェクトを保存（初回保存用）
      saveNewProject: async () => {
        const { saveDataAsync } = get();
        return await saveDataAsync();
      },

      // 新規プロジェクト作成（データ付き）
      createNewProjectWithData: async () => {
        const { products, criteria, evaluationPositions, grades, evaluationTitle, currentExaminationId } = get();
        
        // 既にIDがある場合は既存プロジェクトの更新として扱う
        if (currentExaminationId) {
          console.warn('既にプロジェクトIDが存在します。既存プロジェクトの更新を実行します。');
          return await get().updateExistingProject();
        }
        
        set({ isCreatingProject: true });
        
        try {
          const title = evaluationTitle?.trim() || '無題の評価';
          
          console.log('新規プロジェクト作成中:', { title, productsLength: products.length, criteriaLength: criteria.length });
          const examination = await apiClient.createExamination(title, products);
          console.log('プロジェクト作成完了:', examination.summaryId);
          
          // IDを設定（これにより次回から既存プロジェクトとして扱われる）
          set({ currentExaminationId: examination.summaryId });
          
          // 観点データがある場合は保存
          if (criteria.length > 0) {
            const saveCriteria = get().convertCriteriaForSave(criteria, products, evaluationPositions, grades);
            const result = await apiClient.saveData([], saveCriteria, title, examination.summaryId);
            
            if (!result?.isSuccess) {
              throw new Error('観点データの保存に失敗しました');
            }
          }
          
          set({ 
            isLoading: false, 
            isCreatingProject: false,
            lastSaved: new Date(),
            hasUnsavedChanges: false
          });
          
          console.log('新規プロジェクト作成完了、ID:', examination.summaryId);
          return true;
          
        } catch (error) {
          console.error('新規プロジェクト作成エラー:', error);
          set({ 
            isCreatingProject: false,
            isLoading: false,
            error: `新規プロジェクトの作成に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`
          });
          return false;
        }
      },

      // 既存プロジェクト更新
      updateExistingProject: async () => {
        const { products, criteria, evaluationPositions, grades, evaluationTitle, currentExaminationId } = get();
        
        if (!currentExaminationId) {
          throw new Error('プロジェクトIDが見つかりません');
        }
        
        try {
          const saveCriteria = get().convertCriteriaForSave(criteria, products, evaluationPositions, grades);
          const result = await apiClient.saveData(products, saveCriteria, evaluationTitle, currentExaminationId);
          
          if (result?.isSuccess) {
            set({ 
              isLoading: false,
              lastSaved: new Date(),
              hasUnsavedChanges: false
            });
            return true;
          } else {
            throw new Error(result?.message || '保存に失敗しました');
          }
          
        } catch (error) {
          console.error('プロジェクト更新エラー:', error);
          set({ 
            isLoading: false,
            error: `プロジェクトの更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`
          });
          return false;
        }
      },

      // 観点データ変換ヘルパー
      convertCriteriaForSave: (criteria: CriterionData[], products: ReviewData[], evaluationPositions: Map<string, { x: number; y: number }>, grades: Map<string, GradeData[]>) => {
        return criteria.map((criterion, index) => {
          const xList: number[] = [];
          const yList: number[] = [];

          // 各評価対象について、この観点での位置を取得
          products.forEach((_, productIndex) => {
            const position = evaluationPositions.get(`${productIndex}-${index}`);
            xList.push(position?.x ?? -1);
            yList.push(position?.y ?? -1);
          });

          return {
            saveCriterion: criterion.title,
            saveDescription: criterion.description,
            saveXList: xList,
            saveYList: yList,
            saveGrade: grades.get(index.toString()) || []
          };
        });
      },

      // 新しい評価プロジェクト作成
      createNewEvaluation: () => {
        set({
          ...initialState,
          isLoading: false,
          error: null,
          lastSaved: null,
          evaluationTitle: '',
          currentExaminationId: null,
          hasUnsavedChanges: false
        });
      },

      // 特定の評価プロジェクト読み込み（安全版）
      loadEvaluationById: async (id: string) => {
        const { setProducts, setCriteria, setCurrentExaminationId, setEvaluationTitle, setEvaluationPosition, setGrades, setRubricData, setRubricAnalysis, setCurrentStep } = get();
        
        // 既に同じIDを読み込み中の場合はスキップ
        if (get().isLoading && get().currentExaminationId === id) {
          console.log('既に同じプロジェクトを読み込み中です');
          return true;
        }
        
        set({ isLoading: true, error: null });
        const originalState = { ...get() }; // バックアップ

        try {
          console.log('プロジェクト読み込み開始:', id);
          const result = await apiClient.loadProjectForStore(id);

          if (!result) {
            throw new Error('プロジェクトデータを取得できませんでした');
          }

          console.log('プロジェクトデータ読み込み成功:', result);
          
          // データを段階的に設定（失敗時のロールバック用）
          const newState: Partial<EvaluationStore> = {
            hasUnsavedChanges: false,
            lastSaved: new Date()
          };

          // 評価対象データ
          if (result.reviews) {
            setProducts(result.reviews);
            newState.products = result.reviews;
          }

          // 観点データと等級
          if (result.criteriaData) {
            // CriterionSaveDataからCriterionDataに変換
            const convertedCriteria: CriterionData[] = result.criteriaData.map((c: CriterionSaveData) => ({
              title: c.saveCriterion,
              description: c.saveDescription
            }));
            setCriteria(convertedCriteria);
            newState.criteria = convertedCriteria;
            
            // 等級データの復元
            result.criteriaData.forEach((criterion: CriterionSaveData, index: number) => {
              if (criterion.saveGrade && criterion.saveGrade.length > 0) {
                setGrades(index, criterion.saveGrade);
              }
            });
          }

          // 評価位置データ
          if (result.evaluationPositions) {
            result.evaluationPositions.forEach((pos) => {
              setEvaluationPosition(pos.productIndex, pos.criterionIndex, pos.x, pos.y);
            });
          }

          // タイトルとIDを最後に設定
          if (result.saveTitle) {
            setEvaluationTitle(result.saveTitle);
          }
          setCurrentExaminationId(id);

          // ルーブリックデータの読み込み（非同期、失敗しても継続）
          get().loadRubricDataSafely(id);

          // ステップの自動判定
          const autoDetectedStep = detectCurrentStep(
            get().products, 
            get().criteria, 
            get().evaluationPositions,
            get().grades,
            false // ルーブリックは後で読み込まれるため一旦false
          );
          setCurrentStep(autoDetectedStep);

          set({ isLoading: false, ...newState });
          console.log('プロジェクト読み込み完了');
          
          // 読み込み完了後に進捗状況を表示
          get().showProgress();
          
          return true;
          
        } catch (error) {
          console.error('プロジェクト読み込みエラー:', error);
          
          // 読み込み失敗時は元の状態を維持（データ消失を防ぐ）
          set({ 
            isLoading: false,
            error: `プロジェクトの読み込みに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}。元のデータは保持されています。`
          });
          
          return false;
        }
      },

      // ルーブリックデータの安全な読み込み
      loadRubricDataSafely: async (id: string) => {
        try {
          const rubricData = await apiClient.loadRubric(id);
          if (rubricData.rubric && rubricData.rubric.length > 0) {
            get().setRubricData(rubricData.rubric);
            
            if (rubricData.analysis) {
              rubricData.analysis.forEach((analysisItem: any) => {
                get().setRubricAnalysis(analysisItem.criterionIndex, JSON.parse(analysisItem.data));
              });
            }
            
            // ルーブリック読み込み完了後にステップを再判定
            const autoDetectedStep = detectCurrentStep(
              get().products, 
              get().criteria, 
              get().evaluationPositions,
              get().grades,
              true
            );
            get().setCurrentStep(autoDetectedStep);
          }
        } catch (error) {
          console.warn('ルーブリックデータの読み込みをスキップ:', error);
          // ルーブリック読み込み失敗は非致命的エラー
        }
      }
    })
);

// セレクター関数（簡略化）
export const useCurrentStep = () => useEvaluationStore(state => state.currentStep);
export const useProducts = () => useEvaluationStore(state => state.products);
export const useCriteria = () => useEvaluationStore(state => state.criteria);
export const useError = () => useEvaluationStore(state => state.error);
export const useIsLoading = () => useEvaluationStore(state => state.isLoading);
export const useLastSaved = () => useEvaluationStore(state => state.lastSaved);

// デバッグ用: コンソールから進捗を確認できるようにする
if (typeof window !== 'undefined') {
  (window as Window & { showEvaluationProgress?: () => void }).showEvaluationProgress = () => {
    useEvaluationStore.getState().showProgress();
  };
  console.log('💡 ヒント: コンソールで showEvaluationProgress() を実行すると、現在の進捗状況を確認できます');
}
